import { useState } from 'react';
import type { JSX } from 'react';
// eslint-disable-next-line no-restricted-imports
import { Button, Icon, Label, Select, SelectOption, useToast } from '@wistia/ui';
import type { AlternateAudioFile } from '~/graphql';
import { isNonEmptyString } from '~/utilities/type-guards';
import { useDownloadAlternateAudioFileMutation } from '~/singlePageApp/screens/MediaCustomize/Customize/AudioDescriptions/__gql/AlternateAudioFiles.gql.gen';
import { trackStructEvent } from '~/utilities/snowplow';
import { SelectWrapper, InlineSelectWrapper } from './SharedStyledComponents';

type Props = {
  alternateAudioFiles: AlternateAudioFile[];
};

export const AudioDescriptionDownload = ({ alternateAudioFiles }: Props): JSX.Element => {
  const showToast = useToast();
  const [fileId, setFileId] = useState(alternateAudioFiles[0]?.id ?? '');
  const onChange = (value: string) => setFileId(value);
  const [downloadAlternateAudioFile] = useDownloadAlternateAudioFileMutation();

  const handleDownload = () => {
    void downloadAlternateAudioFile({
      variables: {
        input: {
          id: fileId,
        },
      },
      onCompleted: (data) => {
        const url = data.downloadAlternateAudioFile?.downloadUrl;

        if (isNonEmptyString(url)) {
          window.open(url, '_blank');

          trackStructEvent({
            category: 'button',
            action: 'click',
            label: 'publish-download-audio-files-for-video',
          });
        } else {
          throw new Error('No download URL returned');
        }
      },
      onError: (err) => {
        // eslint-disable-next-line no-console
        console.error(err);
        showToast({
          message: 'Error downloading audio description',
          colorScheme: 'error',
        });
      },
    });
  };

  return (
    <SelectWrapper>
      <Label>Audio descriptions</Label>
      <InlineSelectWrapper>
        <Select
          onChange={onChange}
          fullWidth={true}
          value={fileId}
        >
          {alternateAudioFiles.map(({ id, mediaFileDetails }) => (
            <SelectOption
              key={id}
              value={id}
            >
              {mediaFileDetails.languageMetadata.name}
            </SelectOption>
          ))}
        </Select>
        <Button
          onClick={handleDownload}
          leftIcon={<Icon type="download" />}
          disabled={fileId === ''}
          data-testid="download-audio-description"
        >
          Download
        </Button>
      </InlineSelectWrapper>
    </SelectWrapper>
  );
};
